import { ImageDetails } from "@tylertech/cj-inmate-search-angular-sdk";
import { ForgeImageUrls } from "../modules/forge-proxy/forge-proxy.module";

const offlineImages: string[] = [ ForgeImageUrls.OfflineError ];

export function toMugshotDetails(imageDetails: ImageDetails | null | undefined): ImageDetails | null {
  const urls = imageDetails?.imageUrls;
  return urls?.size320 || urls?.size520 || urls?.size800 ? imageDetails! : null;
}

export function preloadOfflineImages() {
  offlineImages.forEach(i => {
    const img= new Image();
    img.src = i;
  });
}

/** Accepted MIME types for custom Service Box images. */
export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg'] as const;

/** Maximum allowed custom image size in bytes (10MB). */
export const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

/** Square pixel dimension used by the Service Box icon preview. */
export const SERVICE_BOX_IMAGE_SIZE = 80;

export interface ImageValidationResult {
  valid: boolean;
  /** Present only when {@link valid} is false. */
  error?: string;
}

/**
 * Validates that an uploaded file is an accepted image type and within the
 * size limit. Files that are exactly the limit or larger are rejected.
 */
export function validateImageFile(file: File): ImageValidationResult {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type as typeof ACCEPTED_IMAGE_TYPES[number])) {
    return { valid: false, error: 'Unsupported file type. Please upload a PNG or JPEG image.' };
  }
  if (file.size >= MAX_IMAGE_SIZE_BYTES) {
    return { valid: false, error: 'Invalid file. Please make sure the file is an image and is less than 10MB.' };
  }
  return { valid: true };
}

/**
 * Reads a File into a data URL and re-renders it onto a square canvas of
 * {@link SERVICE_BOX_IMAGE_SIZE}px, preserving aspect ratio with a contained
 * fit (letterboxed, transparent background). Returns a PNG data URL suitable
 * for direct display and persistence in the Service Box config.
 */
export function resizeImageToSquare(
  file: File,
  size: number = SERVICE_BOX_IMAGE_SIZE
): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read image file.'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Failed to load image.'));
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas is not supported in this environment.'));
          return;
        }

        // Contain the image within the square, preserving aspect ratio.
        const scale = Math.min(size / img.width, size / img.height);
        const drawWidth = img.width * scale;
        const drawHeight = img.height * scale;
        const dx = (size - drawWidth) / 2;
        const dy = (size - drawHeight) / 2;

        ctx.clearRect(0, 0, size, size);
        ctx.drawImage(img, dx, dy, drawWidth, drawHeight);
        resolve(canvas.toDataURL('image/png'));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}